import { formatPrice } from '@uturn/portal/utils';
import { formatDatetime } from '@uturn/utilities/browser';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import {
	type DateFieldKey,
	FIELD_KEYS,
	isPublishStateFieldKey,
	type PublishStateFieldKey,
} from './field-keys';
import {
	type AddressLike,
	type Carrier,
	type FieldOption,
	type fixedPriceOptionsType,
	INPUT_TYPES,
	type InputType,
	type Primitive,
	type Row,
} from './multi-update-types';

export const buildFixedPriceOptions = (t: TFunction): fixedPriceOptionsType => [
	{
		id: 'quote_accept',
		value: true,
		label: t(
			'pages.addshipment.manual.carrier_can.quote_accept.label',
			'Quote or accept',
		),
	},
	{
		id: 'quote_only',
		value: false,
		label: t(
			'pages.addshipment.manual.carrier_can.quote_only.label',
			'Quote only',
		),
	},
];

/**
 * Both directions offer both statuses, mirroring the acceptance-price select.
 * The stored value is always the target `publish` flag, so the payload mapping
 * never has to invert a label's polarity.
 */
export const buildPublishStateOptions = (
	t: TFunction,
): fixedPriceOptionsType => [
	{
		id: 'publish_state_published',
		value: true,
		label: t(
			'pages.multi-update-shipments.publish-state.published',
			'Published',
		),
	},
	{
		id: 'publish_state_drafted',
		value: false,
		label: t('pages.multi-update-shipments.publish-state.drafted', 'Drafted'),
	},
];

export const PUBLISH_STATE_BASELINE: Record<PublishStateFieldKey, boolean> = {
	[FIELD_KEYS.PUBLISH]: false,
	[FIELD_KEYS.UNPUBLISH]: true,
};

export const getDefaultValueForType = (type?: FieldOption['inputType']) => {
	switch (type) {
		case 'multi-select':
			return [];
		case 'date':
		case 'location':
		case 'boolean':
			return undefined;
		default:
			return ''; // text, price
	}
};

// normalize to the UI type
const enumToUi: Record<string, InputType> = {
	PRICE: 'price',
	TEXT: 'text',
	DATE: 'date',
	BOOLEAN: 'boolean',
	MULTI_SELECT: 'multi-select',
	LOCATION: 'location',
};

const normalizeInputType = (raw: string): InputType => {
	const lower = raw?.toLowerCase();
	if (INPUT_TYPES.includes(lower as InputType)) {
		return lower as InputType;
	}
	const key = lower.replace(/-/g, '_').toUpperCase(); // e.g. multi-select -> MULTI_SELECT
	return enumToUi[key] ?? 'text';
};

export const toFieldOptions = (
	api: { name: string; inputType: string }[],
): FieldOption[] =>
	api.map(({ name, inputType }) => ({
		name,
		inputType: normalizeInputType(inputType),
	}));

export const removeFilterPrefix = (obj: Record<string, unknown>) =>
	Object.fromEntries(
		Object.entries(obj).map(([k, v]) => [k.replace(/^filter\./, ''), v]), // removeFilterPrefix
	);

const toLocalTimestampString = (v: unknown): string => {
	if (!v) {
		return '';
	}
	const d = dayjs(v instanceof Date ? v : String(v));
	return d.isValid() ? d.format('YYYY-MM-DDTHH:mm:ss') : '';
};

/* -------------------- current value formatting -------------------- */

const carrierNames = (values: Array<{ name: string }>): string =>
	values.map((v) => v.name).join(', ');

export const formatCurrentValue = (
	values: Primitive[],
	type: string,
	currencyCode: string = 'EUR',
	fixedPriceOptions?: fixedPriceOptionsType,
): string => {
	if (values.length === 0) {
		return '';
	}
	if (values.length > 1 && type !== 'multi-select') {
		return 'MIXED';
	}

	const value = values[0];

	if (type === 'date') {
		return formatDatetime(value);
	}

	if (type === 'price') {
		const numericAmount = Number(value);
		const amount = Number.isFinite(numericAmount) ? numericAmount : 0;
		return formatPrice(amount, currencyCode);
	}

	if (type === 'boolean') {
		return fixedPriceOptions?.find((item) => item.value === value)?.label;
	}

	if (type === 'multi-select') {
		return carrierNames(values);
	}

	return String(value ?? '');
};

/* -------------------- canonicalization for "no change" logic -------------------- */

export const extractCarrierIds = (values: Carrier[]): number[] =>
	values.map((v) => Number(v.id)).filter((n) => Number.isFinite(n));

export const canonicalizeCurrentValue = (
	values: Array<string | number | any[]>,
	type: string,
): string => {
	if (!values || values.length === 0) {
		return '';
	}
	if (values.length > 1 && type !== 'multi-select') {
		return 'MIXED';
	}

	const v = values[0];

	switch (type) {
		case 'date': {
			return toLocalTimestampString(values[0]);
		}
		case 'price': {
			const n = Number(v);
			return Number.isFinite(n) ? String(n) : '';
		}
		case 'boolean':
			return String(Boolean(v));
		case 'multi-select': {
			const ids = extractCarrierIds(values);
			return `[${ids.join(',')}]`;
		}
		default:
			return String(v ?? '').trim();
	}
};

export const canonicalizeNewValue = (value: unknown, type: string): string => {
	switch (type) {
		case 'date': {
			return toLocalTimestampString(value);
		}
		case 'price': {
			const n = Number(value);
			return Number.isFinite(n) ? String(n) : '';
		}
		case 'boolean': {
			if (value === undefined || value === null || value === '') {
				return '';
			}
			if (value === true || value === 'true' || value === 1 || value === '1') {
				return 'true';
			}
			if (
				value === false ||
				value === 'false' ||
				value === 0 ||
				value === '0'
			) {
				return 'false';
			}
			return '';
		}
		case 'multi-select': {
			const arr = Array.isArray(value) ? value : [];
			const ids = arr
				.map((v) => Number(v))
				.filter((n) => !Number.isNaN(n))
				.sort((a, b) => a - b);
			return `[${ids.join(',')}]`;
		}
		default:
			return String(value ?? '').trim();
	}
};

/* -------------------- location signatures -------------------- */

const norm = (v: unknown) =>
	String(v ?? '')
		.normalize('NFKC')
		.trim()
		.toLowerCase()
		.replace(/\p{White_Space}+/gu, ' ');

const normPostal = (v: unknown) =>
	String(v ?? '')
		.normalize('NFKC')
		.replace(/\p{White_Space}+/gu, '')
		.toUpperCase();

export const locationSignatureFromAddress = (
	a?: AddressLike | null,
): string => {
	if (!a) {
		return '';
	}
	return [
		norm(a.city),
		norm(a.street),
		norm(a.houseNumber),
		normPostal(a.postalCode),
	].join('|');
};
/** Current value shape: [city, street, houseNumber, postalCode] */
const splitLocationParts = (raw: string): [string, string, string, string] => {
	// Normalize whitespace
	const s = String(raw ?? '')
		.normalize('NFKC')
		.replace(/\s+/g, ' ')
		.trim();

	const normalized = s
		.replace(/[,;\-–—]{2,}/g, ',') // any run (2 or more) of comma/semicolon/hyphen/en-dash/em-dash becomes a single comma.
		.replace(/^[,;\-–—\s]+|[,;\-–—\s]+$/g, ''); // removes punctuation/space at the start or end of the string.

	const postalMatch = normalized.match(/\b([0-9]{4}\s?[A-Z]{2})\b/i);
	const postal = postalMatch
		? postalMatch[1].toUpperCase().replace(/\s+/g, '')
		: '';
	const withoutPostal = postalMatch
		? normalized.replace(postalMatch[1], '').trim()
		: normalized;

	const parts = withoutPostal
		.split(',')
		.map((p) => p.trim())
		.filter(Boolean);

	if (parts.length >= 3) {
		const [city, street, houseNumber] = parts;
		return [city, street, houseNumber, postal];
	}

	if (parts.length === 2) {
		const [city, streetAndNo] = parts;
		const m = streetAndNo.match(/^(.*?)[\s-]*([0-9]+[A-Za-z]?)$/);
		return m
			? [city, m[1].trim(), m[2], postal]
			: [city, streetAndNo, '', postal];
	}

	if (parts.length === 1) {
		return [parts[0], '', '', postal];
	}

	return ['', '', '', postal];
};
export const locationSignatureFromCurrentArray = (
	values: Array<string | number | any[]> | undefined,
): string => {
	if (!values || values.length === 0) {
		return '';
	}

	const isEmpty = (v: unknown) =>
		v == null || (typeof v === 'string' && v.trim() === '');

	if (values.length === 1 && typeof values[0] === 'string') {
		const [city, street, houseNumber, postal] = splitLocationParts(
			String(values[0]),
		);
		return locationSignatureFromAddress({
			city,
			street,
			houseNumber,
			postalCode: postal,
		});
	}

	if (
		values.length >= 2 &&
		typeof values[0] === 'string' &&
		isEmpty(values[1]) &&
		isEmpty(values[2]) &&
		isEmpty(values[3])
	) {
		const [city, street, houseNumber, postal] = splitLocationParts(
			String(values[0]),
		);
		return locationSignatureFromAddress({
			city,
			street,
			houseNumber,
			postalCode: postal,
		});
	}

	const city = String(values[0] ?? '');
	const street = String(values[1] ?? '');
	const houseNumber = String(values[2] ?? '');
	const postal = String(values[3] ?? '');

	return locationSignatureFromAddress({
		city,
		street,
		houseNumber,
		postalCode: postal,
	});
};

/* -------------------- id helpers -------------------- */

const normalizeIds = (v: unknown): number[] =>
	Array.isArray(v)
		? Array.from(new Set(v.map(Number).filter(Number.isFinite))).sort(
				(a, b) => a - b,
			)
		: [];

export const sameIds = (a: unknown, b: unknown) => {
	const aa = normalizeIds(a);
	const bb = normalizeIds(b);
	if (aa.length !== bb.length) {
		return false;
	}
	for (let i = 0; i < aa.length; i++) {
		if (aa[i] !== bb[i]) {
			return false;
		}
	}

	return true;
};

/* -------------------- multi-update payload -------------------- */

const convertToCamelCase = (input: string): string =>
	input
		.trim()
		.replace(/[\s_-]+(.)/g, (_, ch: string) => ch.toUpperCase())
		.replace(/^(.)/, (m: string) => m.toLowerCase());

const LOCATION_TYPE_BY_PREFIX: Record<string, string> = {
	'pick-up': 'PICKUP',
	drop: 'DROP',
	deliver: 'DELIVER',
	load: 'LOAD',
	stop: 'STOP',
};

/**
 * Build the request payload:
 * - normal keys are camelCased
 * - location keys are grouped under `locations[]` by locationType
 */
const locationKeyPattern =
	/^(pick-up|drop|deliver|load|stop)-(date-from|date-until|reference|location)$/;

const toLocalIso = (v: unknown): string | undefined => {
	const d = dayjs(v instanceof Date ? v : String(v));
	return d.isValid() ? d.format() : undefined; // keeps local/embedded offset
};

const toUtcIso = (v: unknown): string | undefined => {
	const d = dayjs(v instanceof Date ? v : String(v));
	return d.isValid() ? d.utcOffset(0).format() : undefined; // force +00:00
};

export const mapRowsToMultiUpdateRequestPayload = (
	rows: Array<{ key: string; value?: unknown }>,
): Record<string, unknown> => {
	const topLevelPayload: Record<string, unknown> = {};
	const locationAccumulatorByType: Record<string, Record<string, unknown>> = {};

	for (const { key, value } of rows) {
		const locMatch = key.match(locationKeyPattern);
		const allowEmpty =
			locMatch?.[2] === 'reference' || /(^|-)reference$/.test(key);

		// skip empties unless this is a *reference* field (we keep '' to clear it)
		if (
			(value == null || (typeof value === 'string' && value.trim() === '')) &&
			!allowEmpty
		) {
			continue;
		}

		if (locMatch) {
			const [, locationTypeSlug, subfieldName] = locMatch as RegExpMatchArray;
			const locationType = LOCATION_TYPE_BY_PREFIX[locationTypeSlug];
			if (!locationType) {
				continue;
			}

			const loc =
				locationAccumulatorByType[locationType] ??
				(locationAccumulatorByType[locationType] = { locationType });

			switch (subfieldName) {
				case 'date-from':
					loc.dateFrom = toLocalIso(value);
					break;
				case 'date-until':
					loc.dateUntil = toLocalIso(value);
					break;
				case 'reference':
					loc.reference = String(value ?? '');
					break;
				case 'location':
					if (typeof value === 'number') {
						loc.locationId = value;
					} else {
						loc.googlePlaceId = String(value);
					}
					break;
				default:
			}
			continue;
		}

		// Non-location fields
		const camelKey = convertToCamelCase(key);

		if (
			value instanceof Date ||
			(typeof value === 'string' && dayjs(value).isValid())
		) {
			// Only 'publish-until' → UTC(0)
			topLevelPayload[camelKey] = toUtcIso(value);
			continue;
		}

		if (camelKey === 'carriers' && Array.isArray(value)) {
			// ensure number[]
			topLevelPayload.carriers = value.map(Number).filter(Number.isFinite);
			continue;
		}

		if (camelKey === 'acceptancePrice') {
			topLevelPayload.acceptancePrice =
				value === true || value === 'true' || value === 1 || value === '1';
			continue;
		}

		if (isPublishStateFieldKey(camelKey)) {
			topLevelPayload.publish =
				value === true || value === 'true' || value === 1 || value === '1';
			continue;
		}

		topLevelPayload[camelKey] = value;
	}

	const locations = Object.values(locationAccumulatorByType);
	if (locations.length) {
		topLevelPayload.locations = locations;
	}

	// Sync publish-until with pick-up-date-until
	const pickupUntilRow = rows.find(
		(r) => r.key === FIELD_KEYS.PICKUP_DATE_UNTIL,
	);
	if (pickupUntilRow?.value) {
		topLevelPayload.publishUntil = toUtcIso(pickupUntilRow.value);
	}

	return topLevelPayload;
};

/* -------------------- date validation mapping -------------------- */

const PATH_TO_CANONICAL: Record<string, DateFieldKey> = {
	'locations.0.action.dateFrom': FIELD_KEYS.PICKUP_DATE_FROM,
	'locations.0.action.dateUntil': FIELD_KEYS.PICKUP_DATE_UNTIL,
	'locations.1.action.dateFrom': FIELD_KEYS.DROP_DATE_FROM,
	'locations.1.action.dateUntil': FIELD_KEYS.DROP_DATE_UNTIL,
	publishUntilDate: FIELD_KEYS.PUBLISH_UNTIL,
};

const normalize = (s: string) => s.replace(/[^a-z0-9]/gi, '').toLowerCase();

/**
 * Build a lookup from normalized row key -> { index, actualKey }
 * so we can handle key style differences like "pick-up-date-until" vs "pickup_date_until".
 */
export const buildRowKeyIndex = (rows: Array<{ key?: string }>) => {
	const map = new Map<string, { index: number; actualKey: string }>();
	rows.forEach((r, idx) => {
		if (!r?.key) {
			return;
		}
		map.set(normalize(r.key), { index: idx, actualKey: r.key });
	});
	return map;
};

/** Convert validator path to a canonical multi-update key */
export const pathToCanonicalKey = (path: string): DateFieldKey | undefined =>
	PATH_TO_CANONICAL[path];

/** Apply validator result to RHF inline errors without causing update loops */
export const applyValidatorPathsToFormErrors = ({
	form,
	rows,
	result,
}: {
	form: import('react-hook-form').UseFormReturn<any>;
	rows: Array<{ key?: string }>;
	result:
		| { errors?: Array<{ path?: string; message?: string }> }
		| { valid?: boolean; paths?: string[]; messages?: string[] };
}) => {
	// normalize result to [{path,message}]
	const list: Array<{ path: string; message: string }> = (() => {
		if ('errors' in result && Array.isArray(result.errors)) {
			return result.errors
				.filter(
					(e): e is { path: string; message: string } =>
						!!e?.path && !!e?.message,
				)
				.map((e) => ({ path: e.path, message: e.message }));
		}
		if ('paths' in result && Array.isArray(result.paths)) {
			const msgs = Array.isArray((result as any).messages)
				? (result as any).messages
				: [];
			return result.paths.map((p, i) => ({
				path: String(p),
				message: String(msgs[i] ?? msgs[0] ?? 'Invalid value'),
			}));
		}
		return [];
	})();

	const applied: string[] = [];

	if (!list.length) {
		return applied;
	}

	const indexByRowKey = buildRowKeyIndex(rows);

	for (const { path, message } of list) {
		const canonical = pathToCanonicalKey(path);
		if (!canonical) {
			continue;
		}

		// find the actual row key/index present in the form (tolerate different separators)
		const lookup = indexByRowKey.get(normalize(canonical));
		if (!lookup) {
			continue; // user didn't add this field -> skip
		}

		const name = `rows.${lookup.index}.value` as const;
		applied.push(name);
		const current = form.getFieldState(name).error?.message;

		// only set if message changed (prevents infinite update loops)
		if (current !== message) {
			form.setError(name, { type: 'manual', message });
		}
	}

	return applied;
};

export const toPrimitiveArray = (resp: any): Primitive[] => {
	const v = resp?.data ?? resp;
	if (Array.isArray(v?.value)) {
		return v.value as Primitive[];
	}
	if (Array.isArray(v?.values)) {
		return v.values as Primitive[];
	}
	if (Array.isArray(v)) {
		return v as Primitive[];
	}
	return [];
};

/**
 * Safely read currentValues length from a rowMap by DateFieldKey.
 * Returns 0 if row or currentValues is missing.
 */
export const currentValuesLength = (
	rowMap: Map<string, Row>,
	key: DateFieldKey,
): number => {
	const cv = rowMap.get(key)?.currentValues;
	return Array.isArray(cv) ? cv.length : 0;
};

/** Is the row comparable (i.e. we have a single-baseline value)? */
export const isComparable = (
	baselineArr: unknown[],
	inputType: FieldOption['inputType'],
) =>
	baselineArr.length > 0 &&
	!(baselineArr.length > 1 && inputType !== 'multi-select');

/** Generic “no change” check across input types. */
export const isNoChange = (
	inputType: FieldOption['inputType'],
	baselineArr: unknown[],
	newValue: unknown,
	locationSignature?: string | undefined,
): boolean => {
	switch (inputType) {
		case 'date': {
			const curr = canonicalizeCurrentValue(baselineArr, 'date');
			const next = canonicalizeNewValue(newValue, 'date');
			return !!curr && !!next && curr === next;
		}
		case 'price': {
			const curr = canonicalizeCurrentValue(baselineArr, 'price');
			const next = canonicalizeNewValue(newValue, 'price');
			return !!curr && !!next && curr === next;
		}
		case 'boolean': {
			const curr = canonicalizeCurrentValue(baselineArr, 'boolean');
			const next = canonicalizeNewValue(newValue, 'boolean');
			return !!curr && !!next && curr === next;
		}
		case 'multi-select': {
			const currentIds = extractCarrierIds(baselineArr as any[]);
			const nextRaw = Array.isArray(newValue) ? newValue : [];
			const nextIds = nextRaw.map(Number).filter((n) => !Number.isNaN(n));
			return (
				currentIds.length > 0 &&
				nextIds.length > 0 &&
				sameIds(currentIds, nextIds)
			);
		}
		case 'location': {
			const currSig = locationSignatureFromCurrentArray(baselineArr);
			const nextSig =
				typeof locationSignature === 'string' ? locationSignature.trim() : '';
			return currSig !== '' && nextSig !== '' && currSig === nextSig;
		}
		default: {
			// 'text' or anything else: compare canonicalized strings
			const curr = canonicalizeCurrentValue(baselineArr, 'text');
			const next = canonicalizeNewValue(newValue, 'text');
			return !!curr && !!next && curr === next;
		}
	}
};
